<script setup lang="ts">
import type { CSSProperties } from 'vue'

interface IProps {
  variant?: ButtonVariant
  iconPosition?: 'left' | 'right'
  backgroundColor?: string
  buttonStyle?: CSSProperties
  textStyle?: CSSProperties
  enabled?: boolean
  disabled?: boolean
  loading?: boolean
  type?: 'button' | 'submit' | 'reset'
  hiddeText?: boolean
}

interface IEmits {
  (event: 'click', e: MouseEvent): void
}

const {
  variant = 'primary',
  iconPosition = 'left',
  enabled = true,
  ...props
}
  = defineProps<IProps>()

defineEmits<IEmits>()
const isLoading = toRef(props, 'loading')

const showSaving = ref(isLoading.value)
const timeoutId = ref<NodeJS.Timeout | null>(null)

watch(isLoading, (isLoading) => {
  if (isLoading) {
    if (timeoutId.value)
      clearTimeout(timeoutId.value)
    showSaving.value = true
  }
  else {
    timeoutId.value && clearTimeout(timeoutId.value)
    timeoutId.value = setTimeout(() => {
      showSaving.value = false
    }, 1500)
  }
})
</script>

<script lang="ts">
// eslint-disable-next-line @typescript-eslint/semi
export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'text' | 'clear';

export default {
  name: 'AppButton',
}
</script>

<template>
  <button
    class="center gap-1
    py-2 px-2.5
    rounded-full
    duration-125 font-inter disabled:bg-soft-gray disabled:text-dark-gray disabled:border disabled:border-softGray disabled:cursor-not-allowed disabled:opacity-80"
    :type="type"
    :class="variant"
    :disabled="!enabled || disabled"
    :style="{
      backgroundColor,
      cursor: enabled ? 'pointer' : 'not-allowed',
      flexDirection: iconPosition === 'right' ? 'row-reverse' : 'row',
      ...buttonStyle,
    }"
    @click="($event) => {
      if (enabled && !isLoading) $emit('click', $event)
    }"
  >
    <slot name="icon" />

    <Text v-if="$slots.default && !hiddeText" :style="textStyle" color="inherit" b-0>
      <span v-if="showSaving">
        <slot name="loading">
          Loading...
        </slot>
      </span>
      <slot v-else />
    </Text>
  </button>
</template>

<style lang="scss" scoped>
.primary {
  @apply bg-black text-white;
  @apply border border-black;
}

.secondary {
  @apply bg-brand-gray text-black;
  @apply border border-bluish;
}

.outline {
  // Default and active
  @apply outline-none b-1 border-soft-gray color-bluish-darker;
  // Hover:
  @apply hover:bg-gray-lighter hover:border-bluish-softer hover:color-soft-black;
  // Active:
  @apply active:border-soft-black active:bg-soft-black active:text-white;
}

.outlineDisabled {
  @apply border-gray-lighter bg-gray-lighter color-bluish-softer;
}

.clear {
  @apply outline-none;
}

.text {
  @apply bg-transparent text-black;
  @apply outline-none border border-transparent;
}
</style>
